import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

interface DraggableBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  snapPoints?: number[]; // Percentages of viewport height
  defaultSnapIndex?: number; // Index of default snap point
  closeOnBackdropClick?: boolean; // Whether clicking the backdrop closes the sheet (default: true)
  className?: string;
}

export function DraggableBottomSheet({
  open,
  onOpenChange,
  children,
  snapPoints = [50, 98],
  defaultSnapIndex = 0,
  closeOnBackdropClick = true,
  className,
}: DraggableBottomSheetProps) {
  const [currentSnapIndex, setCurrentSnapIndex] = React.useState(defaultSnapIndex);
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);

  const sheetRef = React.useRef<HTMLDivElement>(null);
  const touchStartRef = React.useRef<{ y: number; time: number; startHeight: number } | null>(null);
  const velocityRef = React.useRef(0);
  const touchHistoryRef = React.useRef<Array<{ y: number; time: number }>>([]);

  const getSnapHeights = React.useCallback(() => {
    const vh = window.innerHeight;
    return snapPoints.map(percent => (vh * percent) / 100);
  }, [snapPoints]);

  const getTargetHeight = React.useCallback(() => {
    const snapHeights = getSnapHeights();
    return snapHeights[currentSnapIndex] || snapHeights[0];
  }, [currentSnapIndex, getSnapHeights]);

  const getCurrentHeight = React.useCallback(() => {
    if (isDragging) {
      const baseHeight = getTargetHeight();
      return Math.max(0, Math.min(window.innerHeight * 0.98, baseHeight - dragOffset));
    }
    return getTargetHeight();
  }, [isDragging, dragOffset, getTargetHeight]);

  const findNearestSnapIndex = React.useCallback(
    (height: number) => {
      const snapHeights = getSnapHeights();
      let nearestIndex = 0;
      let minDistance = Math.abs(snapHeights[0] - height);

      snapHeights.forEach((snapHeight, index) => {
        const distance = Math.abs(snapHeight - height);
        if (distance < minDistance) {
          minDistance = distance;
          nearestIndex = index;
        }
      });

      return nearestIndex;
    },
    [getSnapHeights]
  );

  const determineTargetSnapIndex = React.useCallback(
    (currentHeight: number, velocity: number, _currentIndex: number) => {
      const VELOCITY_THRESHOLD = 1.5;
      const CLOSE_THRESHOLD = window.innerHeight * 0.15;

      if (currentHeight < CLOSE_THRESHOLD) {
        return -1;
      }

      if (velocity > VELOCITY_THRESHOLD) {
        return snapPoints.length - 1;
      }

      if (velocity < -VELOCITY_THRESHOLD) {
        return -1;
      }

      return findNearestSnapIndex(currentHeight);
    },
    [snapPoints.length, findNearestSnapIndex]
  );

  const handleTouchStart = React.useCallback(
    (e: React.TouchEvent) => {
      const target = e.target as HTMLElement;
      const isInteractiveElement = target.closest('[data-no-drag]');
      const touch = e.touches[0];
      const sheetRect = sheetRef.current?.getBoundingClientRect();
      const isHandleArea = target.closest('[data-drag-handle]');
      const isInTopDragZone =
        sheetRect && touch.clientY >= sheetRect.top && touch.clientY - sheetRect.top <= 44;

      if ((!isHandleArea && !isInTopDragZone) || isInteractiveElement) return;

      const currentTime = Date.now();
      touchStartRef.current = {
        y: touch.clientY,
        time: currentTime,
        startHeight: getCurrentHeight(),
      };
      touchHistoryRef.current = [{ y: touch.clientY, time: currentTime }];
      setIsDragging(true);
      velocityRef.current = 0;
    },
    [getCurrentHeight]
  );

  const handleTouchMove = React.useCallback(
    (e: React.TouchEvent) => {
      if (!touchStartRef.current || !isDragging) return;

      const touch = e.touches[0];
      const currentTime = Date.now();

      touchHistoryRef.current.push({ y: touch.clientY, time: currentTime });

      const VELOCITY_WINDOW = 150;
      touchHistoryRef.current = touchHistoryRef.current.filter(
        t => currentTime - t.time <= VELOCITY_WINDOW
      );

      if (touchHistoryRef.current.length >= 2) {
        const oldest = touchHistoryRef.current[0];
        const newest = touchHistoryRef.current[touchHistoryRef.current.length - 1];
        const deltaY = oldest.y - newest.y;
        const deltaTime = newest.time - oldest.time;

        if (deltaTime > 0) {
          velocityRef.current = deltaY / deltaTime;
        }
      }

      const deltaY = touchStartRef.current.y - touch.clientY;
      setDragOffset(-deltaY);

      e.preventDefault();
    },
    [isDragging]
  );

  const handleTouchEnd = React.useCallback(() => {
    if (!touchStartRef.current || !isDragging) return;

    const currentHeight = getCurrentHeight();
    const velocity = velocityRef.current;

    const targetIndex = determineTargetSnapIndex(currentHeight, velocity, currentSnapIndex);

    if (targetIndex === -1) {
      onOpenChange(false);
    } else {
      setCurrentSnapIndex(targetIndex);
    }

    setIsDragging(false);
    setDragOffset(0);
    touchStartRef.current = null;
    velocityRef.current = 0;
    touchHistoryRef.current = [];
  }, [isDragging, getCurrentHeight, currentSnapIndex, determineTargetSnapIndex, onOpenChange]);

  React.useEffect(() => {
    if (open) {
      setCurrentSnapIndex(defaultSnapIndex);
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => setIsAnimating(false), 200);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, defaultSnapIndex]);

  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;

      const openSheets = document.querySelectorAll<HTMLElement>(
        '[data-draggable-bottom-sheet="open"]'
      );
      const topmostSheet = openSheets.item(openSheets.length - 1);
      if (!topmostSheet || !sheetRef.current?.closest('[data-draggable-bottom-sheet]')) return;
      if (sheetRef.current.closest('[data-draggable-bottom-sheet]') !== topmostSheet) return;

      event.preventDefault();
      onOpenChange(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);

  if (!open && !isAnimating) return null;

  const currentHeight = getCurrentHeight();
  const heightPercentage = (currentHeight / window.innerHeight) * 100;
  const isCardMode =
    !isDragging && snapPoints.length > 1 && currentSnapIndex === 0 && snapPoints[0] < 75;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-draggable-bottom-sheet={open ? 'open' : 'closing'}>
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black transition-opacity duration-200',
          isVisible ? 'opacity-50' : 'opacity-0'
        )}
        style={{
          opacity: isVisible ? Math.min(0.5, (heightPercentage / 100) * 0.5) : 0,
        }}
        onClick={closeOnBackdropClick ? () => onOpenChange(false) : undefined}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        data-card-mode={isCardMode ? 'true' : 'false'}
        className={cn(
          'absolute flex flex-col bg-white shadow-2xl dark:bg-gray-900',
          isCardMode
            ? 'left-2 right-2 bottom-2 rounded-3xl'
            : 'left-0 right-0 bottom-0 rounded-t-3xl',
          !isDragging && 'transition-all duration-200 ease-out',
          className
        )}
        style={{
          height: isVisible ? `${currentHeight}px` : '0px',
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Extra invisible hit area above the sheet for comfortable touch dragging. */}
        <div className="absolute -top-3 left-0 right-0 h-3" data-drag-handle="true" />

        {/* Content */}
        <div className={cn('flex-1 overflow-hidden', isCardMode ? 'rounded-3xl' : 'rounded-t-3xl')}>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
